/**
 * The Settings page's sections and each page's permission key (AD #41) — what more than one feature reads: the
 * Settings page builds its cards on it (`src/features/settings/settingsPages.ts`), the role editor lists the pages
 * under one Settings row in this order, and the user and role pages order the Settings row's pages by it. Data
 * features share lives here, in `src/lib`, because a feature never imports another (dependency-cruiser
 * `features-do-not-import-each-other`).
 */

/** The groups the Settings page sorts its cards into, each under its own heading. */
export type SettingsSectionId = 'administration' | 'tags' | 'email';

export interface SettingsSection {
    readonly id: SettingsSectionId;
    readonly title: string;
    readonly description: string;
}

/** The Settings page's sections, in the order it shows them. A section with no card the session may open is left out. */
export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
    {
        id: 'administration',
        title: 'Users & administration',
        description: 'Who can sign in, and what each role lets them do.',
    },
    {
        id: 'tags',
        title: 'Tags',
        description: 'The tags people choose from on accounts, contacts and leads.',
    },
    {
        id: 'email',
        title: 'Email',
        description: 'Saved text to start an email from.',
    },
];

/** The Users page's key: user, manager and offboarding administration. */
export const SETTINGS_USERS_KEY = 'settings.users.manage';

/** The Roles page's key: every change to a role. */
export const SETTINGS_ROLES_KEY = 'settings.roles.manage';

/** Each tagged entity type's tags-page key. */
export const SETTINGS_TAG_KEYS = {
    account: 'settings.account_tags.manage',
    contact: 'settings.contact_tags.manage',
    lead: 'settings.lead_tags.manage',
} as const;

/** The Email templates page's key: the shared templates. */
export const SETTINGS_EMAIL_TEMPLATES_KEY = 'settings.email_templates.manage';

/** One Settings page as access sees it: its id, its section and its key. */
export interface SettingsPageAccess {
    readonly id: string;
    readonly section: SettingsSectionId;
    readonly permission: string;
}

/** Every Settings page, in the order the Settings page shows them within their sections. */
export const SETTINGS_PAGE_ACCESS: readonly SettingsPageAccess[] = [
    { id: 'users', section: 'administration', permission: SETTINGS_USERS_KEY },
    { id: 'roles', section: 'administration', permission: SETTINGS_ROLES_KEY },
    { id: 'account-tags', section: 'tags', permission: SETTINGS_TAG_KEYS.account },
    { id: 'contact-tags', section: 'tags', permission: SETTINGS_TAG_KEYS.contact },
    { id: 'lead-tags', section: 'tags', permission: SETTINGS_TAG_KEYS.lead },
    { id: 'email-templates', section: 'email', permission: SETTINGS_EMAIL_TEMPLATES_KEY },
];

/** Every Settings page's key, in the pages' order: the sidebar's Settings entry and the role editor read it. */
export const SETTINGS_PAGE_KEYS: readonly string[] = SETTINGS_PAGE_ACCESS.map((page) => page.permission);
